import { getPasskey, type PortalMe, type SecondFactor, type SSOOption } from '@central/api';
import { RecoveryCodeList, color } from '@central/ui';
import { Alert, Anchor, Button, Checkbox, PasswordInput, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { api } from '../api';
import { PasskeySetup, TotpSetup } from '../components/SecondFactorSetup';
import { errorMessage } from '../format';
import { useSignedIn } from '../signedIn';
import { leaveFor, ssoErrorMessage, ssoStartHref } from '../sso';

type Stage =
  | { name: 'credentials' }
  | { name: 'mfa'; methods: SecondFactor[] }
  /** The customer requires MFA and this user has no second factor yet. */
  | { name: 'enroll' };

async function passkeySignIn(trustBrowser?: boolean): Promise<PortalMe> {
  const { publicKey } = await api.auth.webauthnLoginBegin();
  const credential = await getPasskey(publicKey);
  return api.auth.webauthnLoginFinish({
    credential,
    ...(trustBrowser === undefined ? {} : { trustBrowser }),
  });
}

/** Customer sign-in (docs/design/CustomerSignIn.dc.html). */
export function SignIn() {
  const [stage, setStage] = useState<Stage>({ name: 'credentials' });
  const signedIn = useSignedIn();

  return (
    <AuthLayout>
      {stage.name === 'credentials' && (
        <Credentials onStage={(s) => (s ? setStage(s) : signedIn())} onSignedIn={signedIn} />
      )}
      {stage.name === 'mfa' && <SecondStep methods={stage.methods} onDone={signedIn} />}
      {stage.name === 'enroll' && <Enroll onDone={signedIn} />}
    </AuthLayout>
  );
}

/** The sign-in screens' frame: the brand panel beside a 400 px form column. */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexWrap: 'wrap',
        background: color.surface,
        color: color.text,
      }}
    >
      <Hero />
      <section
        style={{
          flex: '999 1 480px',
          maxWidth: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '56px 16px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ width: '100%', maxWidth: 400 }}>{children}</div>
      </section>
    </div>
  );
}

function Hero() {
  return (
    <section
      style={{
        flex: '1 1 420px',
        maxWidth: '100%',
        background: color.portalMark,
        color: color.surface,
        padding: 'clamp(24px, 6vw, 56px)',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 40,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          aria-hidden
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: color.surface,
            color: color.portalMark,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 18,
          }}
        >
          G
        </div>
        <span style={{ fontWeight: 700, fontSize: 18 }}>GoATMS Central</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 440 }}>
        <p
          style={{
            margin: 0,
            fontSize: 'clamp(28px, 4vw, 40px)',
            lineHeight: 1.2,
            fontWeight: 700,
          }}
        >
          Your GoATMS deployments, in one place.
        </p>
        <p style={{ margin: 0, fontSize: 17, lineHeight: '26px', color: color.portalNavActiveBg }}>
          Health and uptime for every environment, the version each one runs, and what your licenses
          cover.
        </p>
      </div>
      <ul
        style={{
          margin: 0,
          paddingLeft: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          fontSize: 15,
          color: color.portalNavActiveBg,
        }}
      >
        <li>Live health and 90-day uptime</li>
        <li>License keys, seats used, renewal dates</li>
        <li>Releases your maintenance plan covers</li>
      </ul>
    </section>
  );
}

export function FormFrame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <h1 style={{ margin: 0, fontSize: 28, lineHeight: '36px' }}>{title}</h1>
      {children}
    </div>
  );
}

function Credentials({
  onStage,
  onSignedIn,
}: {
  /** The next stage, or nothing when login already produced a full session. */
  onStage: (s?: Stage) => void;
  onSignedIn: (me: PortalMe) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useMutation({
    mutationFn: () => api.auth.login({ email: email.trim(), password }),
    onSuccess: (r) => {
      if (r.stage === 'full') onStage();
      else if (!r.methods?.length) onStage({ name: 'enroll' });
      else onStage({ name: 'mfa', methods: r.methods });
    },
  });
  const passkey = useMutation({ mutationFn: () => passkeySignIn(), onSuccess: onSignedIn });
  const { ssoError } = useSearch({ from: '/sign-in' });
  const [ssoNeedsEmail, setSsoNeedsEmail] = useState(false);
  const sso = useMutation({
    mutationFn: (address: string) => api.auth.sso.discover({ email: address }),
    onSuccess: (r, address) => {
      if (r.providers.length !== 1) return;
      const href = ssoStartHref(r.providers[0]!.startUrl, address);
      if (href) leaveFor(href);
    },
  });
  const startSso = () => {
    const address = email.trim();
    setSsoNeedsEmail(!address);
    if (address) sso.mutate(address);
  };
  const error = login.error ?? passkey.error ?? sso.error;

  return (
    <FormFrame title="Sign in">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          login.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        <TextInput
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => {
            setEmail(e.currentTarget.value);
            setSsoNeedsEmail(false);
            sso.reset();
          }}
          error={
            ssoNeedsEmail ? 'Enter your work email to find your organization’s SSO.' : undefined
          }
          required
        />
        <PasswordInput
          label={
            <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              Password
              <Link
                to="/forgot-password"
                search={{ email: email.trim() || undefined }}
                style={{ color: color.link, fontWeight: 500 }}
              >
                Forgot password?
              </Link>
            </span>
          }
          labelProps={{ style: { width: '100%' } }}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          required
        />
        {ssoError && !error && <Alert color="red">{ssoErrorMessage(ssoError)}</Alert>}
        {error && <Alert color="red">{errorMessage(error)}</Alert>}
        <Button type="submit" fullWidth loading={login.isPending}>
          Sign in
        </Button>
      </form>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          color: color.textMuted,
          fontSize: 13,
        }}
      >
        <div style={{ flex: 1, height: 1, background: color.border }} />
        or
        <div style={{ flex: 1, height: 1, background: color.border }} />
      </div>
      <Button
        variant="default"
        fullWidth
        loading={passkey.isPending}
        onClick={() => passkey.mutate()}
      >
        Sign in with a passkey
      </Button>
      <Button variant="default" fullWidth loading={sso.isPending} onClick={startSso}>
        Use my organization’s SSO
      </Button>
      {sso.data && <SsoChoice providers={sso.data.providers} email={email.trim()} />}
    </FormFrame>
  );
}

/**
 * What discover found when it was not exactly one provider: none, or a
 * choice. A single provider is opened at once.
 */
function SsoChoice({ providers, email }: { providers: SSOOption[]; email: string }) {
  if (providers.length === 0) {
    return (
      <Alert color="gray">
        Single sign-on is not set up for that email’s domain. Sign in with your password or a
        passkey.
      </Alert>
    );
  }
  if (providers.length === 1) {
    // A usable start has already been opened; a refused one is reported.
    return ssoStartHref(providers[0]!.startUrl, email) ? null : (
      <Alert color="red">{ssoErrorMessage('sso_failed')}</Alert>
    );
  }
  return (
    <div
      role="group"
      aria-label="Choose your sign-on"
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      {providers.map((p) => {
        const href = ssoStartHref(p.startUrl, email);
        return (
          <Button
            key={p.id}
            variant="default"
            fullWidth
            disabled={!href}
            onClick={() => href && leaveFor(href)}
          >
            Continue with {p.name}
          </Button>
        );
      })}
    </div>
  );
}

function TrustBrowser({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <Checkbox
      label="Trust this browser for 7 days"
      checked={checked}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  );
}

function SecondStep({
  methods,
  onDone,
}: {
  methods: SecondFactor[];
  onDone: (me: PortalMe) => void;
}) {
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(false);
  const [recovery, setRecovery] = useState(false);
  const verify = useMutation({
    mutationFn: () =>
      recovery
        ? api.auth.verifyRecoveryCode({ code: code.trim(), trustBrowser: trust })
        : api.auth.verifyTotp({ code: code.trim(), trustBrowser: trust }),
    onSuccess: onDone,
  });
  const passkey = useMutation({
    mutationFn: () => passkeySignIn(trust),
    onSuccess: onDone,
  });
  const error = verify.error ?? passkey.error;
  const switchTo = (r: boolean) => {
    verify.reset();
    setCode('');
    setRecovery(r);
  };
  return (
    <FormFrame title="Confirm it’s you">
      {!recovery && methods.includes('webauthn') && (
        <Button fullWidth loading={passkey.isPending} onClick={() => passkey.mutate()}>
          Use a passkey
        </Button>
      )}
      {(recovery || methods.includes('totp')) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            verify.mutate();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
        >
          <TextInput
            label={recovery ? 'Recovery code' : 'Code from your authenticator app'}
            description={recovery ? 'Each recovery code works once.' : undefined}
            inputMode={recovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            required
          />
          <Button
            type="submit"
            fullWidth
            variant={!recovery && methods.includes('webauthn') ? 'default' : 'filled'}
            loading={verify.isPending}
          >
            Verify
          </Button>
        </form>
      )}
      <TrustBrowser checked={trust} onChange={setTrust} />
      {error && <Alert color="red">{errorMessage(error)}</Alert>}
      {methods.includes('recovery') && (
        <Anchor component="button" type="button" size="sm" onClick={() => switchTo(!recovery)}>
          {recovery ? 'Use your passkey or authenticator app' : 'Use a recovery code'}
        </Anchor>
      )}
    </FormFrame>
  );
}

function Enroll({ onDone }: { onDone: (me: PortalMe) => void }) {
  const [trust, setTrust] = useState(false);
  const [enrolled, setEnrolled] = useState<PortalMe>();
  if (enrolled) return <SaveRecoveryCodes onDone={() => onDone(enrolled)} />;
  return (
    <FormFrame title="Set up a second factor">
      <p style={{ margin: 0, fontSize: 14, lineHeight: '21px', color: color.textBody }}>
        Your organization requires a second sign-in step. Add a passkey or an authenticator app to
        continue.
      </p>
      <TrustBrowser checked={trust} onChange={setTrust} />
      <PasskeySetup onDone={setEnrolled} trustBrowser={trust} />
      <TotpSetup onDone={setEnrolled} trustBrowser={trust} />
    </FormFrame>
  );
}

/** After a first factor, while the session has just proved it: offer recovery codes. */
function SaveRecoveryCodes({ onDone }: { onDone: () => void }) {
  const generate = useMutation({ mutationFn: () => api.auth.generateRecoveryCodes() });
  const codes = generate.data?.codes;
  return (
    <FormFrame title="Save recovery codes">
      <p style={{ margin: 0, fontSize: 14, lineHeight: '21px', color: color.textBody }}>
        A recovery code signs you in once if you lose your passkey or authenticator app. Store them
        somewhere safe; they are shown only once.
      </p>
      {codes ? (
        <>
          <RecoveryCodeList codes={codes} />
          <Button fullWidth onClick={onDone}>
            I’ve saved them, continue
          </Button>
        </>
      ) : (
        <>
          {generate.error && <Alert color="red">{errorMessage(generate.error)}</Alert>}
          <Button fullWidth loading={generate.isPending} onClick={() => generate.mutate()}>
            Create recovery codes
          </Button>
          <Button fullWidth variant="default" onClick={onDone}>
            Skip for now
          </Button>
        </>
      )}
    </FormFrame>
  );
}
